import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
    collectionGroup, 
    query, 
    where, 
    onSnapshot, 
    doc, 
    updateDoc, 
    deleteDoc, 
    serverTimestamp 
} from "firebase/firestore";

// ◄ CORRECCIÓN: Rutas relativas recalibradas de forma precisa a la profundidad real de la carpeta
import { db } from '../../../../firebase/config';
import { useAuth } from '../../../hooks/useAuth'; 

import { useTableState } from '../hooks/useTableState';
import { useDataHydration } from '../hooks/useDataHydration';

export const useSiniestrosTable = () => {
    const { user, profile } = useAuth();
    
    // --- ESTADO DE BASE DE DATOS CRUDA ---
    const [siniestros, setSiniestros] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- ESTADOS DE CONTROL DE MODALES ---
    const [activeItem, setActiveItem] = useState(null);
    const [activePolizaData, setActivePolizaData] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPolicyModal, setShowPolicyModal] = useState(false);
    const [showFichaModal, setShowFichaModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [alertConfig, setAlertConfig] = useState({ show: false, title: '', message: '', type: 'success' });

    // REFS DE CONTROL DE CONCURRENCIA
    const activeItemPathRef = useRef(null);

    // --- 1. HOOK DE HIDRATACIÓN EN BACKGROUND ---
    const { clientsCache, policiesCache } = useDataHydration(siniestros);

    // --- 2. LISTENER DE FIRESTORE LIMITADO A 365 DÍAS ---
    useEffect(() => {
        const limiteFecha = new Date();
        limiteFecha.setDate(limiteFecha.getDate() - 365); // 365 días atrás exactos
        const isoStringLimite = limiteFecha.toISOString().split('T')[0];

        const q = query(
            collectionGroup(db, 'siniestros'),
            where('fechaCarga', '>=', isoStringLimite)
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            const raw = snap.docs.map(d => {
                const parts = d.ref.path.split('/');
                return { 
                    id: d.id, 
                    path: d.ref.path, 
                    clienteId: parts[3], 
                    polizaId: parts[5], 
                    ...d.data() 
                };
            });

            setSiniestros(raw);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // --- 3. MAPEO E HISTORIAL DE ENTIDADES (WATERFALL FALLBACK) ---
    const itemsTable = useMemo(() => {
        return siniestros.map(s => {
            const pKey = `${s.clienteId}|${s.polizaId}`;
            const p = policiesCache[pKey] || {};
            const c = clientsCache[s.clienteId] || {};
            
            let nombreC = (s.clienteNombre || p.clienteNombre || '').trim();
            let apellidoC = (s.clienteApellido || p.clienteApellido || '').trim();
            let displayNombre = `${nombreC} ${apellidoC}`.trim();
            
            if (!displayNombre) {
                displayNombre = c.persona === 'JURIDICA' 
                    ? (c.nombre || '').toUpperCase() 
                    : `${c.nombre || ''} ${c.apellido || ''}`.trim().toUpperCase();
            }
            if (!displayNombre) displayNombre = 'S/D';

            const displayDoc = s.clienteCuit || s.clienteDni || p.clienteCuit || p.clienteDni || c.cuit || c.dni || '-';
            const displayCanalNombre = s.canalNombre || p.canalNombre || s.subcanal || '-';

            const rawEstado = String(s.estadoGestion || 'Abierto').trim();
            const estadoGestion = rawEstado.charAt(0).toUpperCase() + rawEstado.slice(1).toLowerCase();

            return {
                ...s,
                displayNombre,
                displayDoc,
                nPoliza: s.nPoliza || p.nPoliza || 'PENDIENTE',
                compania: s.compania || p.compania || 'S/C',
                riesgo: s.riesgo || p.riesgo || 'S/R',
                subriesgo: s.subriesgo || p.subriesgo || '',
                displayCanalNombre,
                estadoGestion, 
                canal: s.canal || p.canal || '-'
            };
        });
    }, [siniestros, clientsCache, policiesCache]);

    // --- 4. HOOK DE CONTROL SÍNCRONO DE TABLAS ---
    const tableState = useTableState({
        data: itemsTable,
        initialSort: { key: 'fechaCarga', direction: 'descending' },
        initialColumns: {
            fechaCarga: true, fechaSiniestro: true, displayNombre: true, displayDoc: true, 
            nPoliza: true, compania: true, riesgo: true, tipoSiniestro: true, 
            quienGestiono: true, numeroSiniestro: true, estadoGestion: true, canal: true
        },
        initialFilters: {
            estadoGestion: ['Abierto'] // Inicializa el filtro únicamente en Abierto
        },
        recordsPerPage: 100
    });

    // Desbloqueo manual/automático
    const unlockPoliza = useCallback(async (path) => {
        if (!path) return;
        try { 
            await updateDoc(doc(db, path), { bloqueo: null }); 
            if (activeItemPathRef.current === path) activeItemPathRef.current = null;
        } catch (e) { console.error("Error al liberar candado:", e); }
    }, []);

    // --- 5. INTERCEPTOR DE CONTROLADORES ---
    const handleAction = useCallback(async (item, type) => {
        const rawItem = siniestros.find(s => s.id === item.id);
        setActiveItem(rawItem);

        if (type === 'edit' || type === 'poliza') {
            const pKey = `${item.clienteId}|${item.polizaId}`;
            const pData = policiesCache[pKey] || { id: item.polizaId, clienteId: item.clienteId };
            setActivePolizaData(pData);

            if (type === 'edit') {
                setShowEditModal(true);
                activeItemPathRef.current = item.path;
                await updateDoc(doc(db, item.path), { 
                    bloqueo: { userId: user?.uid, userName: profile?.userName || 'Usuario', timestamp: serverTimestamp() } 
                }).catch(() => { activeItemPathRef.current = null; });
            } else if (type === 'poliza') {
                setShowPolicyModal(true);
            }
        } else if (type === 'ficha') {
            setShowFichaModal(true);
        } else if (type === 'delete') {
            setShowDeleteConfirm(true);
        }
    }, [siniestros, policiesCache, user, profile]);

    const handleSuccess = (msg) => {
        setShowEditModal(false);
        unlockPoliza(activeItem?.path);
        setActiveItem(null);
        setAlertConfig({ show: true, title: '¡Éxito!', message: msg, type: 'success' });
    };

    const confirmDelete = async () => {
        try {
            await deleteDoc(doc(db, activeItem.path));
            setShowDeleteConfirm(false);
            setActiveItem(null);
            setAlertConfig({ show: true, title: 'Eliminado', message: 'El siniestro ha sido eliminado.', type: 'success' });
        } catch (e) { console.error(e); }
    };

    return {
        siniestros,
        loading,
        clientsCache,
        activeItem,
        activePolizaData,
        showEditModal,
        showPolicyModal,
        showFichaModal,
        showDeleteConfirm,
        alertConfig,
        tableState,
        itemsTable,
        activeItemPathRef,
        setShowEditModal,
        setShowPolicyModal,
        setShowFichaModal,
        setShowDeleteConfirm,
        setAlertConfig,
        setActiveItem,
        setActivePolizaData,
        handleAction,
        handleSuccess,
        confirmDelete,
        unlockPoliza
    };
};